import React, { useEffect, useState, useMemo } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Globe2, Clock, Wifi } from "lucide-react";

/**
 * Formats a Date into "HH:mm:ss · zzz" for a given IANA timezone.
 */
function tzTime(date, tz) {
  try {
    return new Intl.DateTimeFormat("en-GB", {
      timeZone: tz,
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false,
      timeZoneName: "short",
    }).format(date);
  } catch {
    return "—";
  }
}

function tzDate(date, tz) {
  try {
    return new Intl.DateTimeFormat("en-GB", {
      timeZone: tz,
      weekday: "short",
      day: "2-digit",
      month: "short",
    }).format(date);
  } catch {
    return "";
  }
}

/**
 * TopBar — sticky app-wide header showing:
 *   • Real-time clock (browser local timezone)
 *   • Real-time clock (user's preferred timezone from Settings)
 *   • Public IP + ISP + Country (fetched once from ipapi.co)
 */
export default function TopBar() {
  const { user } = useAuth();
  const [now, setNow] = useState(new Date());
  const [ipInfo, setIpInfo] = useState(null);

  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    let ignore = false;
    (async () => {
      try {
        const cached = sessionStorage.getItem("__ipinfo");
        if (cached) {
          setIpInfo(JSON.parse(cached));
          return;
        }
        const r = await fetch("https://ipapi.co/json/", { cache: "no-store" });
        if (!r.ok) throw new Error("ip lookup failed");
        const j = await r.json();
        if (ignore) return;
        const info = {
          ip: j.ip || "",
          city: j.city || "",
          region: j.region || "",
          country: j.country_name || j.country || "",
          country_code: j.country_code || "",
          org: j.org || j.asn || "",
        };
        sessionStorage.setItem("__ipinfo", JSON.stringify(info));
        setIpInfo(info);
      } catch {
        setIpInfo({ ip: "—", city: "", region: "", country: "", org: "" });
      }
    })();
    return () => { ignore = true; };
  }, []);

  const browserTz = useMemo(() => {
    try { return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC"; } catch { return "UTC"; }
  }, []);
  const userTz = user?.timezone || "UTC";
  const showUserTz = userTz && userTz !== browserTz;

  return (
    <div
      className="sticky top-0 z-30 flex items-center gap-3 px-4 lg:px-6 h-11 border-b border-border bg-white/85 backdrop-blur-md text-xs"
      data-testid="app-topbar"
    >
      {/* Browser local clock */}
      <div className="flex items-center gap-2 min-w-0" data-testid="topbar-local-clock">
        <Clock className="h-3.5 w-3.5 text-emerald-brand shrink-0" strokeWidth={1.75} />
        <div className="flex items-baseline gap-2 min-w-0">
          <span className="font-mono font-medium tabular-nums text-foreground">{tzTime(now, browserTz)}</span>
          <span className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground truncate hidden sm:inline">
            Local · {tzDate(now, browserTz)}
          </span>
        </div>
      </div>

      {/* User-preferred timezone clock */}
      {showUserTz && (
        <>
          <span className="h-4 w-px bg-border" aria-hidden />
          <div className="flex items-center gap-2 min-w-0" data-testid="topbar-user-clock">
            <Globe2 className="h-3.5 w-3.5 text-yellow-600 shrink-0" strokeWidth={1.75} />
            <div className="flex items-baseline gap-2 min-w-0">
              <span className="font-mono font-medium tabular-nums text-foreground">{tzTime(now, userTz)}</span>
              <span className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground truncate hidden md:inline">
                {userTz}
              </span>
            </div>
          </div>
        </>
      )}

      {/* IP · ISP · Country widget */}
      <div className="ml-auto flex items-center gap-2 min-w-0" data-testid="topbar-ip-widget">
        <Wifi className="h-3.5 w-3.5 text-crimson shrink-0" strokeWidth={1.75} />
        {ipInfo ? (
          <div className="flex items-center gap-2 min-w-0">
            <span className="font-mono text-foreground truncate max-w-[120px]" title={ipInfo.ip} data-testid="topbar-ip">{ipInfo.ip || "—"}</span>
            {(ipInfo.org || ipInfo.country) && <span className="h-3 w-px bg-border" />}
            {ipInfo.org && <span className="text-muted-foreground truncate max-w-[220px] hidden lg:inline" title={ipInfo.org} data-testid="topbar-isp">{ipInfo.org}</span>}
            {(ipInfo.country || ipInfo.city) && (
              <span className="text-muted-foreground truncate max-w-[180px]" data-testid="topbar-country">
                {[ipInfo.city, ipInfo.country_code || ipInfo.country].filter(Boolean).join(" · ")}
              </span>
            )}
          </div>
        ) : (
          <div className="h-3 w-32 rounded-sm ai-shimmer" />
        )}
      </div>
    </div>
  );
}
